import React, { FC, useEffect, useState, useCallback } from 'react';
import { useEnvironment, WidgetProps, useBi } from '@wix/yoshi-flow-editor';
import { faqAppLoads, faqQuestionExpanded } from '@wix/bi-logger-labs-data/v2';
import { pauseHeightChanges } from '@wix/yoshi-flow-editor/wix-height-updater';
import { TPAComponentsProvider } from '@wix/wix-ui-tpa/cssVars';
import { ISettingsContextValue } from '@wix/tpa-settings';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { st, classes } from './Widget.st.css';

import HeaderWrapper from './HeaderWrapper/HeaderWrapper';
import TabsWrapper from './Tabs/TabsWrapper';
import QuestionsWrapper from './QuestionsWrapper/QuestionsWrapper';
import SearchWrapper from './SearchWrapper/SearchWrapper';
import { AppContext } from './SettingsProvider/SettingsProvider';
import settingsParams from '../settingsParams';
import constants from '../../../constants';
import { AccordionCSS } from './QuestionsWrapper/Interfaces';
import { QuestionEntry } from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import { ErrorMessage } from './ErrorHandler/ErrorMessage';
import { DATA_HOOKS } from '../../../constants/dataHooksElements';
import stylesParams from '../stylesParams';
import { ControllerProps } from '../../../types/ControllerProps';
import { useLayoutType } from '../../../hooks/useLayoutType';
import { CategoriesPosition } from '../../../enums';

const Widget: FC<WidgetProps<ControllerProps>> = ({
  instance,
  instanceId,
  baseURL,
  api: appAPI,
  initialData,
  questionId,
  fromSearch,
  host,
  listCategoriesResponse,
  listQuestionsResponse,
  fetchError,
  searchTermResponse,
  isSEO = false,
  isHasQuestions = true,
  isEditor3 = false,
}) => {
  const logger = useBi();
  const settings: ISettingsContextValue = useSettings();
  const styles = useStyles();
  const {
    isMobile,
    isEditor,
    multilingual,
    isEditorX: isResponsive,
  } = useEnvironment();
  // Handle RTL languages
  let isRTL = settings.get(settingsParams.textAlignment) === 'right';
  if (multilingual?.isEnabled) {
    const languageCode = multilingual.currentLanguage?.split('-')[0] || '';
    isRTL = constants.RTL_LANGUAGE.includes(languageCode);
  }

  // Extract settings values

  const {
    layout: { layoutValue },
    categoriesPosition,
    displayContent,
    itemAppearance,
    spacing,
  } = useLayoutType(isEditor3);
  let { vertical, horizontal } = spacing;
  if (isResponsive && isMobile) {
    if (vertical === 0 && horizontal === 0) {
      vertical = 0;
      horizontal = 20;
    }
  }

  const showTitleStyleParam = styles.get?.(stylesParams.showTitle) ?? true;
  const showSearchStyleParam = styles.get?.(stylesParams.showSearchBar) ?? true;
  const showSearch =
    showSearchStyleParam && settings.get(settingsParams.searchBar).show;
  const showTitle =
    showTitleStyleParam && settings.get(settingsParams.title).show;
  const showFirstAnswer = settings.get(settingsParams.openFirstAnswer);

  // Define component states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(
    initialData?.selectedCategory,
  );
  const [firstQuestionId, setFirstQuestionId] = useState<string | undefined>(
    questionId,
  );

  const handleSearchChange = useCallback(
    (term: string) => setSearchTerm(term),
    [],
  );

  // Adjust styles based on settings
  const customStyle: AccordionCSS = {
    '--item-top-bottom-padding': `${vertical}px`,
    '--item-left-right-padding': `${horizontal}px`,
  };
  const showCategoriesSettingsParam =
    displayContent === constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES;
  const showCategoriesStyleParam =
    styles.get?.(stylesParams.showFAQCategories) ?? true;
  const showCategories =
    (showCategoriesSettingsParam && showCategoriesStyleParam) ||
    searchTerm !== '';
  const isSideCategories = categoriesPosition === CategoriesPosition.SIDE;
  const isTopCategories = categoriesPosition === CategoriesPosition.TOP;

  useEffect(() => pauseHeightChanges(), [host]);

  // Log analytics event on load
  useEffect(() => {
    logger.report(
      faqAppLoads({
        num_of_categories_shown: initialData.validLists?.length ?? 0,
        num_of_categories:
          settings.get(settingsParams.manageCategories).categories?.length ?? 0,
        num_of_questions: initialData.questionEntries?.length ?? 0,
        layout_type: layoutValue,
        columns: settings.get(settingsParams.structure),
        category_placement: categoriesPosition,
        isOoi: true,
      }),
    );
  }, [initialData, logger, layoutValue, categoriesPosition, settings]);

  // Handle deep linking and scrolling to the question
  useEffect(() => {
    if (isEditor) {
      setFirstQuestionId(showFirstAnswer ? questionId : undefined);
      return;
    }

    if (!questionId || !fromSearch) {
      return;
    }

    const { questionEntries } = initialData;
    const questionData = questionEntries?.find(
      (q: QuestionEntry) => q.id === questionId,
    );

    if (questionData) {
      const { question, categoryId, sortOrder } = questionData;
      logger.report(
        faqQuestionExpanded({
          type: 'deep link',
          question_id: questionId,
          question_name: question || '',
          category_id: categoryId || '',
          question_order: sortOrder || 0,
          url: baseURL || '',
          isOoi: true,
        }),
      );
    }
  }, [
    fromSearch,
    isEditor,
    questionId,
    showFirstAnswer,
    initialData,
    logger,
    baseURL,
    appAPI,
    host,
  ]);
  useEffect(() => {
    if (!questionId || isEditor || !fromSearch) {
      return;
    }
    const element = document.getElementById(questionId);
    if (element) {
      try {
        host
          ?.scrollTo()
          .then(() => {
            appAPI.scrollToElement(`${questionId}`);
          })
          .catch(() => {
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        const unsubscribe = host?.onSiteReady?.(() => {
          appAPI.scrollToElement(`${questionId}`);
        });
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
        return unsubscribe;
      } catch (error) {
        console.warn('host.scrollTo failed:', error);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [questionId, isEditor, appAPI, host, fromSearch]);
  return (
    <TPAComponentsProvider value={{ rtl: isRTL, mobile: isMobile, seo: isSEO }}>
      <AppContext.Provider
        value={{
          instance,
          instanceId,
          baseURL,
          selectedCategory,
          setSelectedCategory,
          firstQuestionId,
          setFirstQuestionId,
          questionId,
          appAPI,
          searchTerm,
          setSearchTerm,
          listCategoriesResponse,
          listQuestionsResponse,
          searchTermResponse,
          fetchError,
          isEditor3,
        }}
      >
        <div
          data-hook={DATA_HOOKS.WIDGET_ROOT}
          dir={isRTL ? 'rtl' : 'ltr'}
          style={customStyle}
          className={st(classes.container, {
            mobile: isMobile,
            isSideCategoriesPosition:
              searchTerm !== '' ? false : isSideCategories,
            isTopCategoriesPosition: isTopCategories || searchTerm !== '',
            hideSearch: !showSearch || !isHasQuestions,
            hideTitle: !showTitle || !isHasQuestions,
            hideCategories: !showCategories || !isHasQuestions,
            theme: itemAppearance,
          })}
        >
          {isHasQuestions && <HeaderWrapper />}
          {isHasQuestions && (
            <SearchWrapper onSearchChange={handleSearchChange} />
          )}
          {isHasQuestions && (
            <TabsWrapper validLists={initialData.validLists} />
          )}
          <QuestionsWrapper
            questions={
              searchTerm && searchTermResponse?.questionEntries
                ? searchTermResponse.questionEntries
                : initialData.questionEntries
            }
          />
          <ErrorMessage error={fetchError} />
        </div>
      </AppContext.Provider>
    </TPAComponentsProvider>
  );
};

export default Widget;
